import { FieldValues } from 'react-hook-form';
import { dateUtils } from 'scbk-shared-module/utils';
import { FieldRule, I18nMessage, RuleContext, ValidateResult } from '../type';

type CustomValidateOptions<TValues> = {
  order?: number;
  deps?: (keyof TValues | string)[];
  when?: (ctx: RuleContext<TValues>) => boolean;
};

/**
 * 폼 유효성 검증 룰 빌더 클래스
 */
export class RuleBuilder<TValues extends FieldValues = any> {
  private _rules: FieldRule<TValues>[] = [];

  /**
   *
   * @param message
   */
  required(
    message: I18nMessage = {
      key: 'modules.form.error.required',
      defaultMessage: '필수 입력 항목 입니다.',
    },
  ) {
    this._rules.push({
      order: 1,
      validate: (v) => (String(v ?? '').length ? true : message),
    });
    return this;
  }

  /**
   * 숫자 최소값(<=)
   * @param min
   * @param message
   */
  min(
    min: number,
    unit = '',
    message: I18nMessage = {
      key: 'modules.form.error.min',
      defaultMessage: '필수 입력 항목 입니다.',
      values: {
        min,
        unit,
      },
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }

        const n = Number(s);
        if (!Number.isFinite(n)) {
          return { key: 'modules.form.error.number' };
        }
        return n >= min ? true : message;
      },
    });
    return this;
  }

  /**
   * 숫자 최대값(>=)
   * @param max 최대 허용가능 숫자
   * @param message
   * @returns
   */
  max(
    max: number,
    unit = '',
    message: I18nMessage = {
      key: 'modules.form.error.max',
      defaultMessage: `${max}${unit} 이하로 입력하세요.`,
      values: {
        max,
        unit,
      },
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }

        const n = Number(s);
        if (!Number.isFinite(n)) {
          return { key: 'modules.form.error.number' };
        }
        return n <= max ? true : message;
      },
    });
    return this;
  }

  /**
   * 숫자 최소/최대 범위 검증
   * @param min 숫자 최소값
   * @param max 숫자 최대값
   * @param message 오류 메시지
   */
  numberRange(
    min: number,
    max: number,
    message: I18nMessage = {
      key: 'modules.form.error.numberRange',
      defaultMessage: `${min} ~ ${max}자 입력하세요.`,
      values: {
        min,
        max,
      },
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }

        const n = Number(s);
        if (!Number.isFinite(n)) {
          return { key: 'modules.form.error.number' };
        }

        const ok = n >= min && n <= max;
        return ok ? true : message;
      },
    });
    return this;
  }

  /**
   * 문자열 최소 길이
   * @param min
   * @param message
   * @returns
   */
  minLength(
    min: number,
    message: I18nMessage = {
      key: 'modules.form.error.minLength',
      defaultMessage: `${min}자 이상 입력하세요.`,
      values: {
        min,
      },
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }
        return s.length >= min ? true : message;
      },
    });
    return this;
  }

  /**
   * 문자열 최대 길이
   * @param max 문자열 최대길이
   * @param message 오류 메시지
   * @returns
   */
  maxLength(
    max: number,
    message: I18nMessage = {
      key: 'modules.form.error.maxLength',
      defaultMessage: `${max}자 이하로 입력하세요.`,
      values: {
        max,
      },
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }
        return s.length <= max ? true : message;
      },
    });
    return this;
  }

  /**
   * 문자열 길이 최소/최대 범위 지정
   * @param min 문자열 최소 길이
   * @param max 문자열 최대 길이
   * @param message 오류 메시지
   */
  lengthRange(
    min: number,
    max: number,
    message: I18nMessage = {
      key: 'modules.form.error.lengthRange',
      defaultMessage: `${min} ~ ${max}자 입력하세요.`,
      values: {
        min,
        max,
      },
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }

        const ok = s.length >= min && s.length <= max;
        return ok ? true : message;
      },
    });
    return this;
  }

  /**
   * 이메일 검증
   * @param message
   * @returns
   */
  email(
    message: I18nMessage = {
      key: 'modules.form.error.email',
      defaultMessage: '이메일 형식이 올바르지 않습니다.',
    },
  ) {
    const regex = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }

        const ok = regex.test(v);
        return ok ? true : message;
      },
    });
    return this;
  }

  /**
   * 패턴기반 검증
   * @param regex 패턴 객체
   * @param message
   * @returns
   */
  pattern(
    regex: RegExp,
    message: I18nMessage = {
      key: 'modules.form.error.pattern',
      defaultMessage: '형식이 올바르지 않습니다.',
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '').trim();
        if (!s.length) {
          return true;
        }

        return regex.test(v) ? true : message;
      },
    });
    return this;
  }

  /**
   * 숫자만 허용가능
   * @param message
   * @returns
   */
  number(
    message: I18nMessage = {
      key: 'modules.form.error.number',
      defaultMessage: '숫자만 입력 가능 합니다.',
    },
  ) {
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }

        return Number.isFinite(Number(v)) ? true : message;
      },
    });
    return this;
  }

  /**
   * 소수허용 자리수 제한
   * @param scale 자리수 (기본값: 2)
   * @param message
   * @returns
   */
  decimal(
    scale = 2,
    message: I18nMessage = {
      key: 'modules.form.error.decimal',
      defaultMessage: `소수점 아래 ${scale}자리까지만 입력 가능 합니다.`,
      values: {
        scale,
      },
    },
  ) {
    const regex = new RegExp(`^\\d+(\\.\\d{1,${scale}})?$`);
    this._rules.push({
      order: 2,
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }

        return regex.test(v) ? true : message;
      },
    });
    return this;
  }

  /**
   * 최소 범위 날짜 검증
   * @param min 최소 날짜형식 yyyyMMdd (기본값: 19000101)
   * @param message
   * @returns
   */
  minDate(
    min: string = '19000101',
    message: I18nMessage = {
      key: 'modules.form.error.minDate',
      defaultMessage: `${dateUtils.getFrmDate(min, '.')} 보다 이전으로 선택할 수 없습니다.`,
      values: {
        min,
      },
    },
  ) {
    this._rules.push({
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }

        if (!/^\d{8}$/g.test(v)) {
          return true;
        }

        return s >= min ? true : message;
      },
    });
    return this;
  }

  /**
   * 최대 범위 날짜 검증
   * @param max 최대 허용가능 날짜형식 yyyyMMdd (기본값: 19000101)
   * @param message
   * @returns
   */
  maxDate(
    max: string = '99991231',
    message: I18nMessage = {
      key: 'modules.form.error.maxDate',
      defaultMessage: `${dateUtils.getFrmDate(max, '.')} 보다 이전으로 선택해야 합니다.`,
      values: {
        max: `${dateUtils.getFrmDate(max, '.')}`,
      },
    },
  ) {
    this._rules.push({
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }

        if (!/^\d{8}$/g.test(v)) {
          return true;
        }

        return s <= max ? true : message;
      },
    });
    return this;
  }

  //한글만/영문만/특수문자금지 처리

  /**
   * 한글만 입력가능
   * @param message
   * @returns
   */
  kor(
    message: I18nMessage = {
      key: 'modules.form.error.kor',
      defaultMessage: '한글만 입력 가능 합니다.',
    },
  ) {
    const regex = new RegExp(
      /[^ㄱ-ㅎㅏ-ㅣ가-힝\,\.\-\u318D\u119E\u11A2\u2022\u2025a\u00B7\uFE55\u318d\u11a2\u119e]/g,
    );
    this._rules.push({
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }
        return regex.test(v) ? message : true;
      },
    });
    return this;
  }

  /**
   * 영문만
   * @param message
   */
  alpha(
    message: I18nMessage = {
      key: 'modules.form.error.alpha',
      defaultMessage: '영문만 입력 가능합니다.',
    },
  ) {
    const regex = new RegExp(/^[a-zA-Z]+$/g);
    this._rules.push({
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }
        return regex.test(v) ? true : message;
      },
    });
    return this;
  }

  /**
   * 영문/숫자만
   * @param case 'upper' | 'lower'
   * @param message
   */
  alphanum(
    message: I18nMessage = {
      key: 'modules.form.error.alphanum',
      defaultMessage: '영문과 숫자만 입력 가능합니다.',
    },
  ) {
    const regex = new RegExp(/^[a-zA-Z0-9]+$/g);
    this._rules.push({
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }
        return regex.test(v) ? true : message;
      },
    });
    return this;
  }

  /**
   * 지정한 특수문자 미허용
   * @param chars 지정 특수문자
   * @param message
   */
  noSpecialChar(
    chars?: string,
    message: I18nMessage = {
      key: 'modules.form.error.noSpecialChar',
      defaultMessage: '허용되지 않는 문자를 입력 하였습니다.',
    },
  ) {
    const escapeCharClass = (ch: string) => {
      return ch.replace(/[\\\-\]\[]/g, '\\$&');
    };
    const forbidden = !chars || chars === '' ? `~\`!@#$%^&*()=+[{]}\\|;:'",<>/?` : chars;
    const escaped = escapeCharClass(forbidden);
    const regex = new RegExp(`[${escaped}]`);
    this._rules.push({
      validate: (v) => {
        const s = String(v ?? '')
          .replace(/,/g, '')
          .trim();
        if (!s.length) {
          return true;
        }

        const result = regex.test(v);
        return result ? message : true;
      },
    });
    return this;
  }

  /**
   *
   * @param fn
   * @param options
   * @returns
   */
  validate(
    fn: (value: string, ctx: RuleContext<TValues>) => ValidateResult,
    options?: CustomValidateOptions<TValues>,
  ) {
    this._rules.push({
      order: options?.order,
      deps: options?.deps,
      when: options?.when,
      validate: fn,
    });

    return this;
  }

  build() {
    return this._rules;
  }
}

export const rulesBuilder = <TValues extends FieldValues = any>() => new RuleBuilder<TValues>();
